import { useLoaderData, useLocation } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    DetailSummaryRow,
    DetailSaveButton,
    detailCta,
    TrackedWebsiteCta,
} from '~/ui-rocco/pages/detail-shared';
import { TileActionPanel } from '~/modules/moodboards/components/TileActionPanel';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';
import {
    ABOUT_PROJECT_ROW_ID,
    useBookMeetingAccordion,
} from '../hooks/use-book-meeting-accordion';
import { BOOK_A_MEETING } from '../hooks/use-project-enquiry-modal';
import { MobileProjectDescription } from './MobileProjectDescription';
import { useAboutProject } from '../hooks/use-about-project';
import { htmlToTeaserText } from '~/ui-rocco/pages/detail-shared/sentence-offsets';

/**
 * The summary's primary button: Book a Meeting on a project, or the enquiry
 * when Book a meeting is not offered to this visitor. Null when neither is.
 */
const useProjectPrimaryCta = () => {
    const { ctas } = usePageCtas();
    const book = useBookCta({ fixedLabel: BOOK_A_MEETING });
    const enquire = useEnquireCta();
    if (ctas.primary === 'book') {
        return { label: book.label, onClick: book.open };
    }
    if (ctas.primary === 'enquire') {
        return { label: enquire.label, onClick: () => enquire.open() };
    }
    return null;
};

/**
 * The desktop cluster — Book a Meeting, Visit Website, Save — level with the
 * summary's first line and sticky from there, as the product page's is: once
 * the page has scrolled it up to 20px under the top it locks and rides the
 * rest of the page.
 *
 * Mounted by the page directly in its main column, just before the summary's
 * band, rather than inside the summary: a sticky element travels only as far
 * as its parent, and the band's wrappers end with the row. The bottom margin
 * gives its height back, so the row starts where it otherwise would; the
 * summary keeps a blank of the same height in the cluster's place.
 */
export const ProjectStickyActions = () => {
    const { project } = useLoaderData<typeof loader>();
    const { Professional } = project;
    const primary = useProjectPrimaryCta();

    return (
        <div className="pointer-events-none sticky top-[1.25rem] z-[120] w-full max-md:hidden md:mb-[-3.25rem] md:px-[3rem]">
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    'flex flex-wrap items-center justify-end gap-2 [&>*]:pointer-events-auto',
                )}
            >
                {primary ? (
                    <button
                        type="button"
                        onClick={primary.onClick}
                        className={detailCta('filled-dark')}
                    >
                        {primary.label}
                    </button>
                ) : null}
                {/* A ground of its own, unlike the shared variant this is
                    built from. The cluster floats over the whole page, not
                    just the dark band it was drawn against, so a fully
                    transparent pill let the description copy run straight
                    through the label — white text and black sentence sharing
                    the same pixels. Dark enough to keep white legible over the
                    light column, sheer and blurred enough to still read as a
                    hairline pill over the banner. */}
                <TrackedWebsiteCta
                    website={Professional.Website}
                    libraryItemID={project.ID}
                    professionalID={Professional.ID}
                    professionalTitle={Professional.Title}
                    source="ProjectWebsiteCTA"
                    variant="outlined-dark"
                    className="bg-black/40 backdrop-blur-md"
                />
                {/* The same ground as its neighbour, for the unsaved
                    glyph: Save rides the whole page in this cluster, and
                    a white outline on nothing is the defect the pill
                    beside it just had. Once saved the button fills white
                    with a dark bookmark; a plain `bg-black/40` would
                    paint over that fill (tailwind-merge keeps one
                    background) and leave a black icon on dark glass. */}
                <DetailSaveButton
                    pinId={project.ID}
                    pinType="ProjectPin"
                    theme="dark"
                    hoverLabel
                    className="backdrop-blur-md not-aria-pressed:bg-black/40"
                />
            </div>
        </div>
    );
};

/**
 * P3 — summary row under the hero. The project description sits on the left;
 * the right `aside` slot of the shared {@link DetailSummaryRow} carries the
 * mobile CTAs, and on desktop keeps the place of the sticky cluster above.
 */
export const ProjectSummarySection = () => {
    const { project, summary } = useLoaderData<typeof loader>();
    // The sheet resolves a relative share against the origin, so an empty
    // string shared the site root rather than this page. The item's own page
    // is the page we are on.
    const { pathname } = useLocation();
    const { Professional } = project;

    // The teaser keeps the copy's paragraph breaks, so a description that
    // opens on a title line doesn't run it into the standfirst. The loader's
    // summary, hard-wrapped for the legacy two-column layout and flattened
    // here, is the fallback when the description's markup yields no text.
    const { contentHtml } = useAboutProject();
    const description =
        htmlToTeaserText(contentHtml ?? '') ||
        `${summary.firstCol} ${summary.secondCol}`.replace(/\s+/g, ' ').trim();

    const { focusAboutProject } = useBookMeetingAccordion();
    const primary = useProjectPrimaryCta();
    const { mobile } = useAppDisplayModeContext();

    return (
        <DetailSummaryRow
            theme="dark"
            // The product page's 28 between Read more and its first CTA.
            contentClassName="max-md:gap-7"
            // No "Introducing {title}" caption — the hero already states the
            // title right above; the eyebrow just repeated it.
            // On the phone the description is the product page's: three
            // lines, then the whole of it and the spec table behind Read
            // more, in place — there is no About section below to send the
            // reader to.
            description={
                mobile ? (
                    <MobileProjectDescription
                        key={project.ID}
                        fallbackText={description || undefined}
                    />
                ) : (
                    description || undefined
                )
            }
            // Level with the CTA row, as the product's and the article's
            // descriptions are.
            // Pre-line only for the desktop teaser's plain text: on the phone
            // the class would reach the expanded description's markup.
            descriptionClassName={cn(
                !mobile && 'whitespace-pre-line',
                'max-md:mt-0 md:mt-0',
            )}
            clampLines={3}
            // "Read more" opens the (01) About-the-Project row (the same copy
            // in full — the row starts collapsed) and scrolls to it, rather
            // than expanding the summary in place. The href stays real for
            // no-JS agents.
            readMoreAnchor={`#${ABOUT_PROJECT_ROW_ID}`}
            onReadMoreAnchorClick={(event) => {
                event.preventDefault();
                focusAboutProject();
            }}
            aside={
                <>
                    {/* Mobile (QDD-first): a full-width primary pill (Book a
                        Meeting, or the enquiry when Book is not offered) over a paired Visit Website / Save row — the
                        product page's cluster, in its white lifted pills at
                        its size. */}
                    <div className="flex w-full flex-col gap-3 md:hidden">
                        {primary ? (
                            <button
                                type="button"
                                onClick={primary.onClick}
                                className={detailCta('white-lifted', 'w-full')}
                            >
                                {primary.label}
                            </button>
                        ) : null}
                        <div className="flex items-stretch gap-2">
                            <TrackedWebsiteCta
                                website={Professional.Website}
                                libraryItemID={project.ID}
                                professionalID={Professional.ID}
                                professionalTitle={Professional.Title}
                                source="ProjectWebsiteCTA"
                                variant="white-lifted"
                                className="flex-1"
                            />
                            {/* ••• in the CTAs' white pill, as the profile
                                sets it: the tile sheet — Share, Save,
                                Save to Pinterest — over the project. */}
                            <TileActionPanel
                                pinId={project.ID}
                                pinType="ProjectPin"
                                bareTrigger
                                sheetLayout="page"
                                triggerClassName={cn(
                                    detailCta('white-lifted'),
                                    'm-0 h-[3.25rem] w-[3.25rem] shrink-0 p-0',
                                )}
                                share={{ url: pathname, title: project.Title }}
                                tileInfo={{
                                    title: project.Title,
                                    subtitle: Professional.Title,
                                    image:
                                        project.Banner?.imageUrl ||
                                        project.FacebookPreviewImage ||
                                        undefined,
                                    imageShape: 'square',
                                }}
                            />
                        </div>
                    </div>

                    {/* Desktop: the cluster's place. It is drawn by the
                        sticky sibling the page mounts above the band, on
                        this exact line. */}
                    <div
                        aria-hidden="true"
                        className="h-[3.25rem] max-md:hidden"
                    />
                </>
            }
        />
    );
};
